import { Option, Result } from '@sapphire/result';

import { Survey } from '../../models/survey.model';
import { SurveyLaunchForm } from '../types/survey-launch-form.types';
import { SurveyUrlsForm } from '../types/survey-urls-form.types';
import { UniqueLinksForm } from '../types/unique-links-form.types';

export type TestAction =
  | { readonly kind: 'open' }
  | { readonly kind: 'save' }
  | {
      readonly kind: 'patch';
      readonly liveUrl: string;
      readonly testUrl: string;
    };

export interface TestEntryLinkDecision {
  /** The survey as last loaded/saved — the baseline the form is compared against. */
  readonly survey: Option<Survey>;
  readonly form: Pick<SurveyLaunchForm, 'surveyUrls' | 'uniqueLinks'>;
}

/**
 * Decides what the TEST button should do next: open the survey's existing test
 * entry link, or first persist what the user changed. Each mode is judged
 * against the survey's own fields, so switching between unique-links and
 * live/test-url mode always reads as changed to the mode being switched into.
 */
export function decideTestAction({
  survey,
  form,
}: TestEntryLinkDecision): Result<TestAction, string> {
  return form.uniqueLinks.uniqueLinksEnabled
    ? decideForUniqueLinks(survey, form.uniqueLinks)
    : decideForSurveyUrls(survey, form.surveyUrls);
}

function decideForUniqueLinks(
  survey: Option<Survey>,
  { entryLinks }: UniqueLinksForm,
): Result<TestAction, string> {
  if (entryLinks.length === 0) {
    return Result.err(
      'Please upload buyer entry links before testing this survey.',
    );
  }

  return resolveAgainstEntryLink(
    survey.filter(
      (activeSurvey) =>
        usesUniqueLinks(activeSurvey) &&
        sameLinks(entryLinksOf(activeSurvey), entryLinks),
    ),
    () => ({ kind: 'save' }),
  );
}

function decideForSurveyUrls(
  survey: Option<Survey>,
  { liveUrl, testUrl }: SurveyUrlsForm,
): Result<TestAction, string> {
  if (!liveUrl) {
    return Result.err('Please enter a Live URL before testing this survey.');
  }

  // Unique links were switched off while the survey still has them on. That
  // toggle only reaches the server through a full save, and until it does the
  // survey's link is still a unique-links one — patching the URLs would leave
  // it in place, so save and open the link that comes back instead.
  if (survey.isSomeAnd(usesUniqueLinks)) {
    return Result.ok({ kind: 'save' });
  }

  return resolveAgainstEntryLink(
    survey.filter(
      (activeSurvey) =>
        (activeSurvey.liveUrl ?? '') === liveUrl &&
        (activeSurvey.testUrl ?? '') === testUrl,
    ),
    () => ({ kind: 'patch', liveUrl, testUrl }),
  );
}

// Shared tail for both modes: a survey that matches the form (baseline-wise)
// opens its existing link; anything else — no match, or a match with no link
// yet — falls to whatever the caller does to catch up (save or patch).
function resolveAgainstEntryLink(
  candidate: Option<Survey>,
  onStale: () => TestAction,
): Result<TestAction, string> {
  return candidate.andThen(testEntryLinkOf).match({
    some: (): Result<TestAction, string> => Result.ok({ kind: 'open' }),
    none: (): Result<TestAction, string> => Result.ok(onStale()),
  });
}

// A survey whose fields match but that carries no link yet still has to be
// persisted — there would be nothing to open otherwise.
function testEntryLinkOf(survey: Survey): Option<string> {
  return Option.from(survey.testPsSurveyEntryLink).filter((link) => !!link);
}

function usesUniqueLinks(survey: Survey): boolean {
  return !!survey.uniqueLinks?.enable;
}

function entryLinksOf(survey: Survey): string[] {
  return (survey.uniqueLinks?.items ?? []).map((item) => item.link);
}

function sameLinks(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((link, index) => link === right[index])
  );
}
